/**
 * Building the multipart body that `save_classification_data` understands.
 *
 * **The multipart filename is the join key**, and that is the single most
 * important fact about this file. The backend re-associates an uploaded file
 * with its metadata row by name and nothing else:
 *
 *     file_class_id, image_name = full_name.split("_", 1)
 *     if file_class_id == class_id and image_name in class_image_map: ...
 *
 * A file whose name does not match is dropped SILENTLY — no error, no log. And
 * the mirror case is worse: the metadata row it should have matched then falls
 * through to the "already uploaded" branch and is inserted with the `url` the
 * client sent, which is `null`. That produces a row that renders as a broken
 * tile and makes training raise inside `extract_object_name_from_url`. The
 * symptom surfaces days later as "the model won't train".
 *
 * So this module returns DESCRIPTORS — `{fields, parts}` — rather than a
 * FormData. A descriptor is inspectable in jest without jsdom, without axios
 * and without a network, which is what lets the tests assert the join in both
 * directions: every metadata row has exactly one file part, and every file part
 * has exactly one metadata row. `toFormData` is the one-line adapter at the end.
 */

/** The backend's reserved class: id 0, named `unstructured`, means UNLABELED. */
export const UNSTRUCTURED_CLASS_ID = 0;
export const UNSTRUCTURED_CLASS_NAME = 'unstructured';

/** Same shape the labeling screens generate. Nothing validates it server-side. */
const randomImageId = () => Math.random().toString(36).slice(2, 11);

const splitExtension = (name) => {
  const dot = String(name || '').lastIndexOf('.');
  if (dot <= 0) return { stem: String(name || ''), ext: '' };
  return { stem: name.slice(0, dot), ext: name.slice(dot) };
};

/** `001.jpg` + 2 -> `001_2.jpg`. Safe: only the FIRST underscore is the split. */
const bumpName = (name, ordinal) => {
  const { stem, ext } = splitExtension(name);
  return `${stem}_${ordinal}${ext}`;
};

/** What the file part is called on the wire, for a given class. */
export const partFilename = (classId, imageName) =>
  Number(classId) === UNSTRUCTURED_CLASS_ID ? imageName : `${classId}_${imageName}`;

/**
 * Assign every item a name that is unique among the FINAL filenames.
 *
 * Deduping per class is not enough on its own. In cluster mode the images past
 * the clustering sample are imported as unstructured alongside real classes, so
 * an unstructured file literally named `1_cat.jpg` could collide with class 1's
 * `cat.jpg`. Uniqueness is therefore enforced on what actually lands in MinIO.
 */
export const assignUniqueNames = (items = []) => {
  const used = new Set();
  return items.map((item) => {
    const classId = Number(item.classId);
    let name = String(item.name || 'image.jpg');
    let filename = partFilename(classId, name);
    let ordinal = 1;
    while (used.has(filename)) {
      ordinal += 1;
      name = bumpName(String(item.name || 'image.jpg'), ordinal);
      filename = partFilename(classId, name);
    }
    used.add(filename);
    return { ...item, classId, name, filename };
  });
};

/**
 * The whole request, as descriptors.
 *
 * `items` is `[{path, name, classId}]` — `path` identifies the bytes inside the
 * zip and never reaches the server.
 */
export const buildClassificationPayload = (plan, items, context = {}) => {
  const makeId = context.makeId || randomImageId;
  if (!plan) throw new Error('A plan is required to build an upload.');
  if (!context.projectId || !context.modelId || !context.ownerId) {
    throw new Error('projectId, modelId and ownerId are required to build an upload.');
  }

  const planClasses = Array.isArray(plan.classes) ? plan.classes : [];
  planClasses.forEach((entry) => {
    // Defence in depth. Class 0 is the unlabeled bucket, and an id containing an
    // underscore would make `split("_", 1)` read a truncated id.
    if (!Number.isInteger(entry.id) || entry.id < 1) {
      throw new Error(`Class id must be an integer of 1 or more, got: ${entry.id}`);
    }
    if (String(entry.id).includes('_')) {
      throw new Error(`Class id must not contain an underscore: ${entry.id}`);
    }
    if (String(entry.name || '').toLowerCase() === UNSTRUCTURED_CLASS_NAME) {
      throw new Error('A class may not be called "unstructured" — that name means unlabeled.');
    }
  });

  const named = assignUniqueNames(items || []);
  const byClass = new Map();
  const unstructured = [];

  named.forEach((item) => {
    const row = {
      id: makeId(),
      name: item.name,
      // The client has no stored URL for something it is uploading right now.
      url: null,
      thumbnailUrl: null,
      isNew: true,
    };
    if (item.classId === UNSTRUCTURED_CLASS_ID) {
      unstructured.push({ ...row, assignedClass: null });
      return;
    }
    if (!byClass.has(item.classId)) byClass.set(item.classId, []);
    byClass.get(item.classId).push({ ...row, assignedClass: item.classId });
  });

  const classes = planClasses.map((entry) => ({
    id: entry.id,
    name: entry.name,
    images: byClass.get(entry.id) || [],
  }));

  const parts = named.map((item) => ({
    field: item.classId === UNSTRUCTURED_CLASS_ID ? 'unstructured_files' : 'class_files',
    path: item.path,
    filename: item.filename,
  }));

  return {
    fields: {
      project_id: context.projectId,
      model_id: context.modelId,
      model_type: 'classification',
      owner_id: context.ownerId,
      // Always a fresh version. Re-saving an existing one HARD-deletes its data
      // items first, and the client cannot reconstruct the stored urls of images
      // it did not upload — so an in-place save would destroy them.
      is_new_version: 'true',
      classes: JSON.stringify(classes),
      // Required, so it must be present even when there is nothing in it.
      unstructured_images: JSON.stringify(unstructured),
    },
    parts,
    counts: {
      classes: classes.length,
      labeled: classes.reduce((sum, entry) => sum + entry.images.length, 0),
      unlabeled: unstructured.length,
      total: named.length,
    },
  };
};

/**
 * Descriptors -> FormData.
 *
 * `model_data_info_id` is deliberately never appended. Appending JS `null` puts
 * the literal string "null" on the wire, and the branch that does not test for
 * that string then calls `ObjectId("null")` and 500s.
 */
export const toFormData = (payload, blobs, FormDataCtor) => {
  const Ctor = FormDataCtor || (typeof FormData !== 'undefined' ? FormData : null);
  if (!Ctor) throw new Error('FormData is unavailable in this environment.');
  const form = new Ctor();
  Object.entries(payload.fields).forEach(([key, value]) => form.append(key, value));
  payload.parts.forEach((part) => {
    const blob = blobs.get(part.path);
    if (!blob) throw new Error(`Missing bytes for ${part.path}`);
    form.append(part.field, blob, part.filename);
  });
  return form;
};

/**
 * The multipart body for `save_detection_data`.
 *
 * Deliberately NOT a variant of the classification builder — the two endpoints
 * differ in almost every respect, and a shared function would hide it:
 *
 *   classification            detection
 *   ------------------------  ---------------------------------------
 *   `unstructured_images`     `images`  (one list, labeled or not)
 *   `class_files`             `image_files`
 *   filename `{classId}_{n}`  filename is the bare `name`
 *   a miss is silently DROPPED a miss is `class_image_map[name]` -> KeyError -> 500
 *
 * That last row is why the manifest and the parts are built in ONE pass from
 * one array here, and why the tests assert the correspondence in both
 * directions. The backend indexes `class_image_map[full_name]["id"]`,
 * `["annotations"]`, `["originalWidth"]` and `["originalHeight"]` with no
 * guard at all, so every one of those keys must be present on every entry.
 */
export const buildDetectionPayload = (plan, items, context = {}) => {
  const makeId = context.makeId || randomImageId;
  if (!plan) throw new Error('A plan is required to build an upload.');
  if (!context.projectId || !context.modelId || !context.ownerId) {
    throw new Error('projectId, modelId and ownerId are required to build an upload.');
  }

  const planClasses = Array.isArray(plan.classes) ? plan.classes : [];
  planClasses.forEach((entry) => {
    // ClassItem.id is `Optional[int]` in the pydantic schema; a non-numeric id
    // raises a ValidationError the route does not catch, i.e. a 500.
    if (!Number.isInteger(entry.id) || entry.id < 1) {
      throw new Error(`Class id must be an integer of 1 or more, got: ${entry.id}`);
    }
  });
  const validIds = new Set(planClasses.map((entry) => entry.id));

  // Detection filenames carry no class prefix, so uniqueness is on the bare
  // name — which still matters: `name` becomes the MinIO key AND the label
  // file's stem at training time, so two `001.jpg` would overwrite each other.
  const named = assignUniqueNames((items || []).map((item) => ({ ...item, classId: UNSTRUCTURED_CLASS_ID })));

  const images = named.map((item, index) => {
    const source = items[index] || {};
    const boxes = Array.isArray(source.annotations) ? source.annotations : [];
    return {
      id: makeId(),
      name: item.name,
      // Never null: `len(item.get("annotations", []))` is called on this, and an
      // explicit JSON null makes that a TypeError, i.e. a 500.
      annotations: boxes
        .filter((box) => validIds.has(box.classId))
        .map((box) => ({
          classId: box.classId,
          centerX: box.centerX,
          centerY: box.centerY,
          width: box.width,
          height: box.height,
          angle: 0,
        })),
      polygons: [],
      url: null,
      thumbnailUrl: null,
      isNew: true,
      // Indexed without a guard by the uploader.
      originalWidth: source.originalWidth || 0,
      originalHeight: source.originalHeight || 0,
    };
  });

  const parts = named.map((item) => ({
    field: 'image_files',
    path: item.path,
    // The bare name — no class prefix. This MUST equal the entry's `name`.
    filename: item.name,
  }));

  const labeled = images.filter((image) => image.annotations.length > 0).length;

  return {
    fields: {
      project_id: context.projectId,
      model_id: context.modelId,
      model_type: 'bounding_box',
      owner_id: context.ownerId,
      is_new_version: 'true',
      // The classes ARRAY ORDER becomes the YOLO class index at training time;
      // `id` is only the join key that annotations point at.
      classes: JSON.stringify(planClasses.map((entry) => ({ id: entry.id, name: entry.name }))),
      images: JSON.stringify(images),
    },
    parts,
    counts: {
      classes: planClasses.length,
      labeled,
      unlabeled: images.length - labeled,
      total: images.length,
      boxes: images.reduce((sum, image) => sum + image.annotations.length, 0),
    },
  };
};
